import { lazy } from "react";
import Loadable from "components/Loadable";
import { authRoles } from "auth/authRoles";
import { pluralize } from "utils/utils";

// Utility function to generate CRUD routes based on options
const createCrudRoutes = (basePath, List, AddEdit, View, auth, options = {}) => {
  const routes = [];
  if (options.list) routes.push({ path: pluralize(basePath), element: <List />, auth });
  if (options.add) routes.push({ path: `${basePath}/add`, element: <AddEdit />, auth });
  if (options.edit) routes.push({ path: `${basePath}/edit/:id`, element: <AddEdit />, auth });
  if (options.view) routes.push({ path: `${basePath}/view/:id`, element: <View />, auth });
  return routes;
};

// Lazy-loaded components
const Analytics = Loadable(lazy(() => import("pages/dashboard/Analytics")));
const NotFound = Loadable(lazy(() => import("pages/error/NotFound")));



// SETTING
const SettingList = Loadable(lazy(() => import("pages/private/setting/List")));
const SettingAddEdit = Loadable(lazy(() => import("pages/private/setting/AddEdit")));
const SettingView = Loadable(lazy(() => import("pages/private/setting/View")));




// MY VENUE
const MyVenueList = Loadable(lazy(() => import("pages/private/my-venue/List")));
const MyVenueAddEdit = Loadable(lazy(() => import("pages/private/my-venue/AddEdit")));
const MyVenueView = Loadable(lazy(() => import("pages/private/my-venue/View")));


// ANCILLARY_SERVICE
const AncillaryServiceList = Loadable(lazy(() => import("pages/private/ancillary-services/List")));
const AncillaryServiceAddEdit = Loadable(lazy(() => import("pages/private/ancillary-services/AddEdit")));
const AncillaryServiceView = Loadable(lazy(() => import("pages/private/ancillary-services/View")));


// MY Booking
const MyBookingList = Loadable(lazy(() => import("pages/private/my-booking/List")));
const MyBookingAddEdit = Loadable(lazy(() => import("pages/private/my-booking/AddEdit")));
const MyBookingView = Loadable(lazy(() => import("pages/private/my-booking/View")));


// Personal Booking
const PersonalBookingList = Loadable(lazy(() => import("pages/private/personal-booking/List")));
const PersonalBookingAddEdit = Loadable(lazy(() => import("pages/private/personal-booking/AddEdit")));
const PersonalBookingView = Loadable(lazy(() => import("pages/private/personal-booking/View")));

// My Bids
const MyBidsList = Loadable(lazy(() => import("pages/private/my-bids/List")));
const MyBidsAddEdit = Loadable(lazy(() => import("pages/private/my-bids/AddEdit")));
const MyBidsView = Loadable(lazy(() => import("pages/private/my-bids/View")));

// Personal Bids
const PersonalBidsList = Loadable(lazy(() => import("pages/private/personal-bids/List")));
const PersonalBidsAddEdit = Loadable(lazy(() => import("pages/private/personal-bids/AddEdit")));
const PersonalBidsView = Loadable(lazy(() => import("pages/private/personal-bids/View")));

// My Service Requests
const MyServiceRequestsList = Loadable(lazy(() => import("pages/private/my-service-requests/List")));
const MyServiceRequestsAddEdit = Loadable(lazy(() => import("pages/private/my-service-requests/AddEdit")));
const MyServiceRequestsView = Loadable(lazy(() => import("pages/private/my-service-requests/View")));

// Personal Service Requests
const PersonalServiceRequestsList = Loadable(lazy(() => import("pages/private/personal-service-requests/List")));
const PersonalServiceRequestsAddEdit = Loadable(lazy(() => import("pages/private/personal-service-requests/AddEdit")));
const PersonalServiceRequestsView = Loadable(lazy(() => import("pages/private/personal-service-requests/View")));

// Venue Transactions
const VenueTransactionsList = Loadable(lazy(() => import("pages/private/venue-transaction/List")));
const VenueTransactionsAddEdit = Loadable(lazy(() => import("pages/private/venue-transaction/AddEdit")));
const VenueTransactionsView = Loadable(lazy(() => import("pages/private/venue-transaction/View")));

// AncillaryService Subscriptions
const AncillaryServiceSubscriptionsList = Loadable(lazy(() => import("pages/private/ancillary-services-subscription/List")));
const AncillaryServiceSubscriptionsAddEdit = Loadable(lazy(() => import("pages/private/ancillary-services-subscription/AddEdit")));
const AncillaryServiceSubscriptionsView = Loadable(lazy(() => import("pages/private/ancillary-services-subscription/View")));

// Venue Review
const VenueReviewList = Loadable(lazy(() => import("pages/private/venue-review/List")));
const VenueReviewAddEdit = Loadable(lazy(() => import("pages/private/venue-review/AddEdit")));
const VenueReviewView = Loadable(lazy(() => import("pages/private/venue-review/View")));

// Service Review
const ServiceReviewList = Loadable(lazy(() => import("pages/private/service-review/List")));
const ServiceReviewAddEdit = Loadable(lazy(() => import("pages/private/service-review/AddEdit")));
const ServiceReviewView = Loadable(lazy(() => import("pages/private/service-review/View")));

// My Review
const MyReviewList = Loadable(lazy(() => import("pages/private/my-review/List")));
const MyReviewAddEdit = Loadable(lazy(() => import("pages/private/my-review/AddEdit")));
const MyReviewView = Loadable(lazy(() => import("pages/private/my-review/View")));

// My Favorite
const MyFavoriteList = Loadable(lazy(() => import("pages/private/my-favorites/List_copy")));
const MyFavoriteAddEdit = Loadable(lazy(() => import("pages/private/my-favorites/AddEdit")));
const MyFavoriteView = Loadable(lazy(() => import("pages/private/my-favorites/View")));
// USER BANK
const UserBankList = Loadable(lazy(() => import("pages/private/user-bank/List")));
const UserBankAddEdit = Loadable(lazy(() => import("pages/private/user-bank/AddEdit")));
const UserBankView = Loadable(lazy(() => import("pages/private/user-bank/View")));



const privateRoutes = [
  { path: "/dashboard", element: <Analytics />, auth: authRoles.admin },
  { path: "*", element: <NotFound /> },

  // User Routes
  ...createCrudRoutes("/setting", SettingList, SettingAddEdit, SettingView, authRoles.admin, { list: true, add: false, edit: false, view: false }),

  // My Venues Routes
  ...createCrudRoutes("/my-venue", MyVenueList, MyVenueAddEdit, MyVenueView, authRoles.admin, { list: true, add: true, edit: true, view: true }),

  // My AncillaryService Routes
  ...createCrudRoutes("/ancillary-service", AncillaryServiceList, AncillaryServiceAddEdit, AncillaryServiceView, authRoles.admin, { list: true, add: true, edit: true, view: true }),


  // My Booking Routes
  ...createCrudRoutes("/my-booking", MyBookingList, MyBookingAddEdit, MyBookingView, authRoles.admin, { list: true, add: true, edit: true, view: true }),


  // Personal Booking Routes
  ...createCrudRoutes("/personal-booking", PersonalBookingList, PersonalBookingAddEdit, PersonalBookingView, authRoles.admin, { list: true, add: true, edit: true, view: true }),

  // My Bids Routes
  ...createCrudRoutes("/my-bid", MyBidsList, MyBidsAddEdit, MyBidsView, authRoles.admin, { list: true, add: true, edit: true, view: true }),


  // Personal Bids Routes
  ...createCrudRoutes("/personal-bid", PersonalBidsList, PersonalBidsAddEdit, PersonalBidsView, authRoles.admin, { list: true, add: true, edit: true, view: true }),
  
  // My Service Requests
  ...createCrudRoutes("/my-service-request", MyServiceRequestsList, MyServiceRequestsAddEdit, MyServiceRequestsView, authRoles.admin, { list: true, add: true, edit: true, view: true }),


  // Personal Service Requests
  ...createCrudRoutes("/personal-service-request", PersonalServiceRequestsList, PersonalServiceRequestsAddEdit, PersonalServiceRequestsView, authRoles.admin, { list: true, add: true, edit: true, view: true }),
  
  // Venue Transactions
  ...createCrudRoutes("/venue-transaction", VenueTransactionsList, VenueTransactionsAddEdit, VenueTransactionsView, authRoles.admin, { list: true, add: true, edit: true, view: true }),


  // AncillaryService Subscriptions
  ...createCrudRoutes("/ancillary-service-subscription", AncillaryServiceSubscriptionsList, AncillaryServiceSubscriptionsAddEdit, AncillaryServiceSubscriptionsView, authRoles.admin, { list: true, add: true, edit: true, view: true }),

  // Venue Review
  ...createCrudRoutes("/venue-review", VenueReviewList, VenueReviewAddEdit, VenueReviewView, authRoles.admin, { list: true, add: true, edit: true, view: true }),
  
  // Service Review
  ...createCrudRoutes("/service-review", ServiceReviewList, ServiceReviewAddEdit, ServiceReviewView, authRoles.admin, { list: true, add: true, edit: true, view: true }),


  // My Review
  ...createCrudRoutes("/my-review", MyReviewList, MyReviewAddEdit, MyReviewView, authRoles.admin, { list: true, add: true, edit: true, view: true }),
 
//  My Favorites
  ...createCrudRoutes("/my-favorite",MyFavoriteList,MyFavoriteAddEdit,MyFavoriteView, authRoles.admin, { list: true, add: true, edit: true, view: true }),



  // User Bank Routes
  ...createCrudRoutes("/user-bank", UserBankList, UserBankAddEdit, UserBankView, authRoles.admin, { list: true, add: true, edit: true, view: true }),






];

export default privateRoutes;

